import { createContext, useContext, useEffect, useState } from 'react';
import type { Product } from '@/mocks/products';
import {
  deleteProduct as deleteProductRecord,
  fetchProducts,
  getFallbackProducts,
  saveProduct as saveProductRecord,
} from '@/lib/api/products';

interface ProductCatalogContextValue {
  products: Product[];
  loading: boolean;
  saving: boolean;
  source: 'api' | 'fallback';
  error: string;
  refresh: () => Promise<void>;
  saveProduct: (product: Product) => Promise<Product>;
  deleteProduct: (productId: string) => Promise<void>;
}

const ProductCatalogContext = createContext<ProductCatalogContextValue | null>(null);

export function ProductCatalogProvider({ children }: { children: React.ReactNode }) {
  const [products, setProducts] = useState<Product[]>(getFallbackProducts());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState<'api' | 'fallback'>('fallback');
  const [error, setError] = useState('');

  const refresh = async () => {
    setLoading(true);

    try {
      const nextProducts = await fetchProducts();
      setProducts(nextProducts.length > 0 ? nextProducts : getFallbackProducts());
      setSource(nextProducts.length > 0 ? 'api' : 'fallback');
      setError('');
    } catch (err) {
      setProducts(getFallbackProducts());
      setSource('fallback');
      setError(err instanceof Error ? err.message : 'Unable to load products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const saveProduct = async (product: Product) => {
    setSaving(true);

    try {
      const saved = await saveProductRecord(product);
      setProducts((prev) => {
        const exists = prev.some((item) => item.id === saved.id);
        const next = exists
          ? prev.map((item) => (item.id === saved.id ? saved : item))
          : [...prev, saved];

        return next.sort((a, b) => a.category.localeCompare(b.category) || a.capacity - b.capacity);
      });
      setSource('api');
      setError('');
      return saved;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save product.');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (productId: string) => {
    setSaving(true);

    try {
      await deleteProductRecord(productId);
      setProducts((prev) => prev.filter((item) => item.id !== productId));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete product.');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProductCatalogContext.Provider
      value={{
        products,
        loading,
        saving,
        source,
        error,
        refresh,
        saveProduct,
        deleteProduct,
      }}
    >
      {children}
    </ProductCatalogContext.Provider>
  );
}

export function useProductCatalog() {
  const ctx = useContext(ProductCatalogContext);

  if (!ctx) {
    throw new Error('useProductCatalog must be used within ProductCatalogProvider');
  }

  return ctx;
}
